import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MENU_CACHE_LIMIT } from './cachedMenu.constants';
import {
  IAddBrandMenuData,
  IAddMenuData,
  ICachedMenuSlice,
  IUpdateBrandMenuData,
  IUpdateMenuData,
} from './cachedMenu.props';
import { IUtterance } from '../../../types/brandMenu';

const initialState: ICachedMenuSlice = {
  menusById: {},
  menusOrder: [],
  brandMenusById: {},
  brandMenusOrder: [],
  brandMenuUtterancesById: {},
  brandMenuUnavailableEntitiesById: {},
  brandMenuMealPeriodsById: {},
};

const cachedMenuSlice = createSlice({
  name: 'cachedMenu',
  initialState,
  reducers: {
    resetCachedMenuData: () => {
      return { ...initialState };
    },
    addMenuData: (state, { payload }: PayloadAction<IAddMenuData>) => {
      const { restaurantId, menuVersion, menuData, menuTitle } = payload;
      if (state.menusOrder.length === MENU_CACHE_LIMIT) {
        // Remove the oldest cached menu restaurant data
        const oldestDetails = state.menusOrder.shift();
        if (oldestDetails?.restaurantId)
          delete state.menusById[oldestDetails.restaurantId];
      }
      state.menusOrder.push({ restaurantId, menuVersion, menuTitle });
      state.menusById[restaurantId] = menuData;
    },
    updateMenuData: (state, { payload }: PayloadAction<IUpdateMenuData>) => {
      const { restaurantId, menuVersion, menuData, index, menuTitle } = payload;
      state.menusOrder[index] = { restaurantId, menuVersion, menuTitle };
      state.menusById[restaurantId] = menuData;
    },
    addBrandMenuData: (
      state,
      { payload }: PayloadAction<IAddBrandMenuData>
    ) => {
      const {
        restaurantId,
        fingerprint,
        brandMenuData,
        restaurantUnavailableEntities,
        restaurantMealPeriods,
      } = payload;
      if (state.brandMenusOrder.length === MENU_CACHE_LIMIT) {
        const oldestDetails = state.brandMenusOrder.shift();
        if (oldestDetails?.restaurantId) {
          delete state.brandMenusById[oldestDetails.restaurantId];
          delete state.brandMenuUtterancesById[oldestDetails.restaurantId];
          delete state.brandMenuUnavailableEntitiesById[
            oldestDetails.restaurantId
          ];
          delete state.brandMenuMealPeriodsById[oldestDetails.restaurantId];
        }
      }
      state.brandMenusOrder.push({ restaurantId, fingerprint });
      state.brandMenusById[restaurantId] = brandMenuData;
      state.brandMenuUnavailableEntitiesById[restaurantId] =
        restaurantUnavailableEntities;
      state.brandMenuMealPeriodsById[restaurantId] = restaurantMealPeriods;
    },
    updateBrandMenuData: (
      state,
      { payload }: PayloadAction<IUpdateBrandMenuData>
    ) => {
      const {
        restaurantId,
        fingerprint,
        brandMenuData,
        index,
        restaurantUnavailableEntities,
        restaurantMealPeriods,
      } = payload;
      state.brandMenusOrder[index] = { restaurantId, fingerprint };
      state.brandMenusById[restaurantId] = brandMenuData;
      state.brandMenuUnavailableEntitiesById[restaurantId] =
        restaurantUnavailableEntities;
      state.brandMenuMealPeriodsById[restaurantId] = restaurantMealPeriods;
    },
    cacheBrandMenuUtterances: (
      state,
      {
        payload,
      }: PayloadAction<{ restaurantId: string; utterances: IUtterance[] }>
    ) => {
      state.brandMenuUtterancesById[payload.restaurantId] = payload.utterances;
    },
  },
});

export const {
  resetCachedMenuData,
  addMenuData,
  updateMenuData,
  addBrandMenuData,
  updateBrandMenuData,
  cacheBrandMenuUtterances,
} = cachedMenuSlice.actions;
export default cachedMenuSlice.reducer;
